import React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import logo from '@/assets/logo.png';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { errorTypes } from '@/utility/validation';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { IResetPasswordForm, IResetPasswordResponse } from '@/interfaces/Auth/forgotPassword.interface';

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword, isSubmitting: isSubmittingPassword },
    watch: watchPassword
  } = useForm<IResetPasswordForm>();

  const onSubmitPassword = async (data: IResetPasswordForm) => {
    if (!token) {
      showToastNotification('danger', 'Invalid or missing reset token.');
      return;
    }
    
    try {
      const response = await callApi<IResetPasswordResponse>(`${API_URL}${API_ENDPOINT.resetPassword}/${token}`, {
        method: 'POST',
        data: { newPassword: data.newPassword },
      });
      showToastNotification('success', response?.message || 'Password reset successfully');
      navigate(RoutesLink.login);
    } catch (error) {
      ThrowError(error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, hsl(var(--primary)) 2px, transparent 2px), radial-gradient(circle at 75% 75%, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-card rounded-xl border shadow-2xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="Yebo Eggs" className="w-24 h-24 mb-3 rounded-full shadow-lg" />
            <h1 className="text-xl font-bold text-foreground">Reset Password</h1>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Please enter your new password below.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmitPassword(onSubmitPassword)}>
            <FormField
              name="New Password"
              type="password"
              className="w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
              {...registerPassword('newPassword', {
                ...errorTypes.required('New Password'),
                minLength: { value: 8, message: 'Password must be at least 8 characters' }
              })}
              requiredIcon={true}
              error={errorsPassword.newPassword?.message}
            />
            <FormField
              name="Confirm Password"
              type="password"
              className="w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
              {...registerPassword('confirmPassword', {
                ...errorTypes.required('Confirm Password'),
                validate: (val: string) => {
                  if (watchPassword('newPassword') != val) {
                    return "Your passwords do no match";
                  }
                }
              })}
              requiredIcon={true}
              error={errorsPassword.confirmPassword?.message}
            />
            <Button type="submit" fullWidth isLoading={isSubmittingPassword} loadingText="Resetting Password...">
              Set New Password
            </Button>
            
            <div className="mt-6 text-center">
              <Link to={RoutesLink.login} className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                Back to Login
              </Link>
            </div>
          </form>
        </div>
        <p className="text-center text-[10px] text-muted-foreground mt-4">© {new Date().getFullYear()} Yebo Eggs (Pty) Ltd · South Africa</p>
      </div>
    </div>
  );
}
